import {groupBy, groupByUniqueKey, objectFromEntries} from '@benchling/js-utils';
import Decimal from 'decimal.js';
import stringify from 'json-stable-stringify';
import _ from 'underscore';

import {assert} from 'Asserts';
import {MeasurementUnit} from 'constants/__generated/MeasurementUnit';
import {Measurement} from 'constants/MeasurementConstants';
import {MinimalSchemaTag} from 'folder-items/FileConstants';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import {convertFromStandard} from 'inventory/InventoryUnitUtil';
import {
  PlateRecordHydratedDataQuery,
  PlateRecordHydratedDataQueryVariables,
} from 'samples/plate-maps/plate-record/__generated/PlateRecordHydratedDataQuery';
import {
  BaseReadonlyPlateWellTransfer,
  ContainerTransferRecord,
  ContainerTransferRecordContainerContent,
  ContainerTransferRecordSource,
  ContainerTransferRecordSourceContainer,
  FrozenEntityChipData,
  FrozenSchemaMetadata,
  isPlateMapMeasurement,
  PersistedPlateMap,
  PlateMapMeasurement,
  PlateRecord,
  PlateRecordMeasurement,
  SupportedFrozenDataParameterValue,
  SupportedFrozenSchemaFieldValue,
  TransferRecordMeasurementUnit,
} from 'samples/plate-maps/plate-record/PlateRecordConstants';
import {PLATE_RECORD_HYDRATED_DATA_QUERY} from 'samples/plate-maps/plate-record/PlateRecordQueryUtil';
import {
  INACCESSIBLE_GROUP_ID_SENTINEL,
  PlateMapSize,
  PlateMap,
  PlateMapWell,
  ReagentAssignmentGroup,
  ReagentType,
  SchematizedAttributeType,
  SchematizedDisplayLayerMetadata,
  Source,
  SupportedFieldType,
  TestOrderInfoById,
  MeasurementLayer,
} from 'samples/plate-maps/PlateMapConstants';
import {getColor, getIconFromFieldType, wellPositionIterator} from 'samples/plate-maps/PlateMapUtil';
import {NO_MATERIAL_INPUT_SENTINEL} from 'samples/plate-maps/transfer-table/FillPlateTransferTableConstants';
import {MeasurementWithErrors} from 'samples/plate-maps/transfer-table/validation/FillPlateTransferTableValidationConstants';
import {
  getDefaultMeasurement,
  getDefaultMeasurementValidationErrors,
  getDefaultMeasurementWithErrors,
} from 'samples/plate-maps/transfer-table/validation/FillPlateTransferTableValidationUtil';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import APIUtil, {apiIdToIconName} from 'util/APIUtil';
import {typeToName} from 'util/NameUtil';
import {omit} from 'util/omitKey';

/**
 * Whether the given source is a container source.
 */
export function isContainerSource<TMeasurement extends PlateRecordMeasurement | PlateMapMeasurement>(
  source: ContainerTransferRecordSource<TMeasurement>
): source is ContainerTransferRecordSourceContainer<TMeasurement> {
  return source.hasOwnProperty('containerApiId');
}

export interface UnitsById {
  [id: string]: {[versionNumber: number]: MeasurementUnit};
}

function getUnitIdsFromContainerTransferRecordContainerContents<
  TMeasurement extends PlateRecordMeasurement | PlateMapMeasurement,
>(
  contents: Array<ContainerTransferRecordContainerContent<TMeasurement>>
): Array<TransferRecordMeasurementUnit | undefined> {
  const displayUnits: Array<TransferRecordMeasurementUnit | undefined> = [];
  contents.forEach((content) => {
    displayUnits.push(content.content.concentration?.displayUnit);
    displayUnits.push(content.finalConcentration?.displayUnit);
  });
  return displayUnits;
}

/**
 * Get all the unit API IDs used in a plate record's concentration and quantity measurements
 * and the API IDs of all sources and contents. Because we show live chips with overridden names
 * in the modal, and want to override if the chip is inaccesible, we need to query for it here
 * even if it has frozen data.
 */
export function getPlateMapHydratedQueryVariables<
  TTransferKey extends string,
  TMeasurement extends PlateRecordMeasurement | PlateMapMeasurement,
>(
  positions: Array<BaseReadonlyPlateWellTransfer<never, TTransferKey, TMeasurement>>,
  transferKey: TTransferKey
): PlateRecordHydratedDataQueryVariables {
  const bioentityIds: Set<string> = new Set();
  const storableIds: Set<string> = new Set();
  const testOrderIds: Set<string> = new Set();
  let displayUnits: Array<TransferRecordMeasurementUnit | undefined> = [];

  for (const position of positions) {
    const transferRecord = position[transferKey];
    displayUnits.push(transferRecord.finalQuantity?.displayUnit);
    for (const {content, testOrders} of transferRecord.contents) {
      if (content.frozenSchemaMetadata == null || content.entityName == null) {
        bioentityIds.add(content.entityApiId);
      }
      for (const to of testOrders ?? []) {
        testOrderIds.add(to.testOrderId);
      }
    }
    for (const source of transferRecord.sources) {
      if (isContainerSource(source)) {
        displayUnits.push(source.transferQuantity?.displayUnit);
        displayUnits = displayUnits.concat(
          getUnitIdsFromContainerTransferRecordContainerContents(source.contents)
        );
        storableIds.add(source.containerApiId);
        for (const {content, testOrders} of source.contents) {
          bioentityIds.add(content.entityApiId);
          for (const to of testOrders ?? []) {
            testOrderIds.add(to.testOrderId);
          }
        }
      } else {
        bioentityIds.add(source.entityApiId);
        for (const to of source.testOrders ?? []) {
          testOrderIds.add(to.testOrderId);
        }
        displayUnits.push(source.transferQuantity?.displayUnit);
        displayUnits.push(source.finalConcentration?.displayUnit);
        displayUnits.push(source.concentration?.displayUnit);
      }
    }
    displayUnits = displayUnits.concat(
      getUnitIdsFromContainerTransferRecordContainerContents(transferRecord.contents)
    );
    if (position.measurementLayerValues != null) {
      displayUnits = displayUnits.concat(
        position.measurementLayerValues.map(
          (measurementLayerValue) => measurementLayerValue.value?.displayUnit
        )
      );
    }
  }

  const dedupedUnits = _.unique(
    displayUnits.filter((unit): unit is TransferRecordMeasurementUnit => unit != null),
    (unit) => stringify(unit)
  );
  return {
    bioentityIds: [...bioentityIds],
    storableIds: [...storableIds],
    unitIds: dedupedUnits.map(({unitApiId}) => unitApiId),
    unitVersionNumbers: dedupedUnits.map(({unitVersionNumber}) => unitVersionNumber),
    testOrderIds: [...testOrderIds],
  };
}

export function getHydratedPlateRecordData(
  plateRecord: PlateRecord
): ReturnType<typeof getHydratedPlateData> {
  return getHydratedPlateData(plateRecord.wellTransferRecords, 'transferRecord');
}

export interface HydratedPlateData {
  liveEntityChipData: {[id: string]: {name: string; iconId?: BenchlingIconName}};
  schemaIdsByReagentId: {[id: string]: string | undefined};
  unitsById: UnitsById;
  testOrderInfoById: TestOrderInfoById;
}

/**
 * Shape a successful `PLATE_RECORD_HYDRATED_DATA_QUERY` response into the maps the
 * plate-record / plate-map renderers expect.
 */
export function hydratedPlateDataFromQueryResult(result: PlateRecordHydratedDataQuery): HydratedPlateData {
  const {bioentities, storables, units, testOrders} = result;
  const accessibleBioentities = accessibleObjs(bioentities);
  const accessibleStorables = accessibleObjs(storables);

  const bioentityEntries: Array<[string, string | undefined]> = accessibleBioentities.map((bioentity) => [
    bioentity.id,
    bioentity.tagSchema?.id,
  ]);
  const storableEntries: Array<[string, string]> = accessibleStorables.map((storable) => [
    storable.id,
    storable.storableSchema.id,
  ]);
  const schemaIdsByReagentId = objectFromEntries([...bioentityEntries, ...storableEntries]);

  const bioentityChipData: Array<[string, {name: string; iconId?: BenchlingIconName}]> =
    accessibleBioentities.map((bioentity) => [
      bioentity.id,
      {name: bioentity.name, iconId: (bioentity.tagSchema?.iconId as BenchlingIconName) ?? undefined},
    ]);
  const storableChipData: Array<[string, {name: string; iconId?: BenchlingIconName}]> =
    accessibleStorables.map((storable) => [
      storable.id,
      {name: storable.name, iconId: storable.storableSchema.iconId as BenchlingIconName},
    ]);
  const liveEntityChipData = objectFromEntries([...bioentityChipData, ...storableChipData]);

  const testOrderInfoById = objectFromEntries(
    accessibleObjs(testOrders).map((testOrder) => [testOrder.id, omit(testOrder, '__typename')])
  );

  const unitsGroupedById = groupBy(units, (unit) => unit.id);
  const unitEntries: Array<[string, {[versionNumber: number]: MeasurementUnit}]> = Object.entries(
    unitsGroupedById
  ).map(([apiId, units]) => {
    const groupedByVersion = groupByUniqueKey(units, (unit) => unit.versionNumber);
    return [apiId, groupedByVersion];
  });
  const unitsById = objectFromEntries(unitEntries);
  return {liveEntityChipData, schemaIdsByReagentId, unitsById, testOrderInfoById};
}

/**
 * Queries for the live data needed to create a plate map from a plate record, including units and
 * any container or bioentity schemas used as sources and contents.
 */
export async function getHydratedPlateData<
  T extends string,
  TMeasurement extends PlateRecordMeasurement | PlateMapMeasurement,
>(
  positions: Array<BaseReadonlyPlateWellTransfer<never, T, TMeasurement>>,
  transferKey: T
): Promise<HydratedPlateData> {
  // TODO(BNCH-107132): Hydrate test order data
  const variables = getPlateMapHydratedQueryVariables(positions, transferKey);
  if (Object.values(variables).every((ids) => ids != null && ids.length === 0)) {
    return {liveEntityChipData: {}, schemaIdsByReagentId: {}, unitsById: {}, testOrderInfoById: {}};
  }
  const result = await doQuerySafe<PlateRecordHydratedDataQuery, PlateRecordHydratedDataQueryVariables>(
    {query: PLATE_RECORD_HYDRATED_DATA_QUERY},
    {variables}
  );
  return hydratedPlateDataFromQueryResult(result);
}

/**
 * Transform the given transfer record measurement to the equivalent legacy measurement.
 */
function hydrateTransferRecordMeasurement<TMeasurement extends PlateRecordMeasurement | PlateMapMeasurement>(
  transferRecordMeasurement: TMeasurement | null,
  unitsById: UnitsById
): Measurement {
  if (transferRecordMeasurement == null) {
    return getDefaultMeasurement();
  }
  const {unitApiId, unitVersionNumber} = transferRecordMeasurement.displayUnit;
  const unit = unitsById[unitApiId][unitVersionNumber];
  assert(unit != null, `Expected to have unit ${unitApiId} with version number ${unitVersionNumber}`);
  if (isPlateMapMeasurement(transferRecordMeasurement)) {
    return {
      value: transferRecordMeasurement.value,
      unit,
    };
  } else {
    return convertFromStandard(new Decimal(transferRecordMeasurement.siValue), unit);
  }
}

/**
 * Transform the given transfer record measurement to the equivalent legacy measurement with default errors.
 */
function hydrateTransferRecordMeasurementWithErrors<
  TMeasurement extends PlateRecordMeasurement | PlateMapMeasurement,
>(transferRecordMeasurement: TMeasurement | null, unitsById: UnitsById): MeasurementWithErrors<Measurement> {
  const measurement = hydrateTransferRecordMeasurement(transferRecordMeasurement, unitsById);
  return {
    measurement,
    errors: getDefaultMeasurementValidationErrors(),
  };
}

/**
 * Transform the given transfer record source to a plate map source.
 */
function getSourceFromTransferRecordSource<TMeasurement extends PlateRecordMeasurement | PlateMapMeasurement>(
  source: ContainerTransferRecordSource<TMeasurement>,
  unitsById: UnitsById,
  schemaIdsByReagentId: {[id: string]: string | undefined}
): Source {
  if (isContainerSource(source)) {
    return {
      type: ReagentType.SOURCE_CONTAINER,
      id: source.containerApiId,
      groupId:
        source.frozenSchemaMetadata?.schemaApiId ??
        schemaIdsByReagentId[source.containerApiId] ??
        INACCESSIBLE_GROUP_ID_SENTINEL,
      quantity: null,
      transferQuantity: hydrateTransferRecordMeasurementWithErrors(source.transferQuantity, unitsById),
      contents: source.contents.map((containerContent) => ({
        type: ReagentType.SOURCE_CONTAINABLE,
        id: containerContent.content.entityApiId,
        groupId:
          containerContent.content.frozenSchemaMetadata?.schemaApiId ??
          schemaIdsByReagentId[containerContent.content.entityApiId] ??
          INACCESSIBLE_GROUP_ID_SENTINEL,
        sourceConcentration: hydrateTransferRecordMeasurementWithErrors(
          containerContent.content.concentration,
          unitsById
        ),
        finalConcentration: hydrateTransferRecordMeasurementWithErrors(
          containerContent.finalConcentration,
          unitsById
        ),
        transferQuantity: getDefaultMeasurementWithErrors(),
        materialInput: {
          materialInputId: containerContent.materialInputParameterVersionId ?? NO_MATERIAL_INPUT_SENTINEL,
          error: null,
        },
        testOrders: containerContent.testOrders?.map(({testOrderId}) => ({id: testOrderId})),
      })),
    };
  } else {
    return {
      type: ReagentType.SOURCE_CONTAINABLE,
      id: source.entityApiId,
      groupId:
        source.frozenSchemaMetadata?.schemaApiId ??
        schemaIdsByReagentId[source.entityApiId] ??
        INACCESSIBLE_GROUP_ID_SENTINEL,
      sourceConcentration: hydrateTransferRecordMeasurementWithErrors(source.concentration, unitsById),
      finalConcentration: hydrateTransferRecordMeasurementWithErrors(source.finalConcentration, unitsById),
      transferQuantity: hydrateTransferRecordMeasurementWithErrors(source.transferQuantity, unitsById),
      materialInput: {
        materialInputId: source.materialInputParameterVersionId ?? NO_MATERIAL_INPUT_SENTINEL,
        error: null,
      },
      testOrders: source.testOrders?.map(({testOrderId}) => ({id: testOrderId})),
    };
  }
}

function castValueToArray(value: string | Array<string>): Array<string> {
  if (Array.isArray(value)) {
    return value;
  }
  if (value === '') {
    return [];
  }
  return [value];
}

function convertValueToLinkTypeArray<T extends object = object>(
  name: string,
  value: string | Array<string>,
  defaultCreator?: (id: string) => T
): Array<{name: string; url: string; id: string} & T> {
  /**
   * TODO(BNCH-97442): We store the name as a joined string.
   * If a customer adds a comma to their entity name, this will break
   */
  const ids = castValueToArray(value);
  const names = name.split(',').map((name) => name.trim());
  if (ids.length !== names.length) {
    return [];
  }

  return ids.map((id, index) => ({
    name: names[index],
    // url is legacy and unused, but it's required for the type.
    url: '',
    id,
    ...(defaultCreator ? defaultCreator(id) : ({} as T)),
  }));
}

export function getTagValue(frozenSchemaFieldValue: SupportedFrozenSchemaFieldValue): MinimalSchemaTag {
  const {schemaFieldValue, frozenMetadata} = frozenSchemaFieldValue;

  const {fieldType} = frozenMetadata;

  const tagValue: MinimalSchemaTag = {
    // Confusingly, value is the display value, and we use the relevant key to store the typed value.
    value: schemaFieldValue.textValue,
    tagSchemaField: {
      name: frozenMetadata.fieldName,
      fieldType: frozenMetadata.fieldType,
    },
  };

  switch (fieldType) {
    case FieldType.TEXT:
      tagValue.value = schemaFieldValue.value as string;
      tagValue.url = schemaFieldValue.url ?? null;
      break;
    case FieldType.DATE:
      tagValue.dateValue = schemaFieldValue.value as string;
      break;
    case FieldType.LONG_TEXT:
      tagValue.value = schemaFieldValue.value as string;
      break;
    case FieldType.INTEGER:
      tagValue.integerValue = schemaFieldValue.value as number;
      break;
    case FieldType.FLOAT:
      tagValue.floatValue = schemaFieldValue.value as number;
      break;
    case FieldType.BOOLEAN:
      tagValue.booleanValue = Boolean(schemaFieldValue.value);
      break;
    case FieldType.DATETIME:
      tagValue.datetimeValue = schemaFieldValue.value as string;
      break;
    case FieldType.SELECTOR:
      tagValue.schemaFieldSelectorOptionIds = castValueToArray(
        schemaFieldValue.value as string | Array<string>
      );
      break;
    case FieldType.JSON:
      tagValue.jsonValue = schemaFieldValue.value as object;
      break;
    case FieldType.BIOENTITY_LINK:
      tagValue.targetFiles = convertValueToLinkTypeArray(
        schemaFieldValue.textValue ?? '',
        schemaFieldValue.value as string | Array<string>
      );
      break;
    case FieldType.ENTRY_LINK:
      tagValue.targetEntries = convertValueToLinkTypeArray(
        schemaFieldValue.textValue ?? '',
        schemaFieldValue.value as string | Array<string>,
        () => ({humanId: null})
      );
      break;
    case FieldType.BLOB_LINK:
      tagValue.targetBlobs = convertValueToLinkTypeArray(
        schemaFieldValue.textValue ?? '',
        schemaFieldValue.value as string | Array<string>,
        () => ({previewFile: null})
      );
      break;
    case FieldType.SAMPLE_LINK:
      tagValue.targetSamples = convertValueToLinkTypeArray(
        schemaFieldValue.textValue ?? '',
        schemaFieldValue.value as string | Array<string>
      );
      break;
    case FieldType.STORABLE_LINK:
      tagValue.targetStorables = convertValueToLinkTypeArray(
        schemaFieldValue.textValue ?? '',
        schemaFieldValue.value as string | Array<string>,
        (id) => ({iconId: apiIdToIconName(id)})
      );
      break;
    default:
      tagValue.value = schemaFieldValue.textValue;
  }
  return tagValue;
}

/**
 * Build a mapping of schema field id to MinimalSchemaTag for the given frozen schema field values.
 */
export function getSchemaTags(frozenSchemaFieldValues: Array<SupportedFrozenSchemaFieldValue>): {
  [fieldId: string]: MinimalSchemaTag;
} {
  return frozenSchemaFieldValues.reduce(
    (acc: {[fieldId: string]: MinimalSchemaTag}, frozenSchemaFieldValue) => {
      acc[frozenSchemaFieldValue.schemaFieldValue.fieldDefinitionId] = getTagValue(frozenSchemaFieldValue);
      return acc;
    },
    {}
  );
}

export function getDataParameterTags(frozenDataParameterValues: Array<SupportedFrozenDataParameterValue>): {
  [fieldId: string]: MinimalSchemaTag;
} {
  return frozenDataParameterValues.reduce(
    (acc: {[fieldId: string]: MinimalSchemaTag}, frozenDataParameterValue) => {
      acc[frozenDataParameterValue.dataParameterVersionId] = getTagValue(
        frozenDataParameterValue.frozenSchemaFieldValue
      );
      return acc;
    },
    {}
  );
}

/**
 * Build a list of display layer dropdown options for the given display layer values.
 * Dedupes on key and filters out unsupported types.
 */
export function getDisplayLayerDropdownOptionsForValues(
  values: Array<{
    key: string;
    fieldName: string;
    fieldType: FieldType;
    displayNameOverride?: string | null;
  }>,
  attributeType: SchematizedAttributeType,
  supportedTypes: Set<FieldType>
): Array<SchematizedDisplayLayerMetadata> {
  const wellAnnotationMap: {[key: string]: SchematizedDisplayLayerMetadata} = {};
  values.forEach(({key, fieldName, fieldType, displayNameOverride}) => {
    if (!supportedTypes.has(fieldType)) {
      return;
    }
    if (wellAnnotationMap[key] != null) {
      return;
    }
    wellAnnotationMap[key] = {
      attribute: {
        key,
        schemaField: {
          name: fieldName,
          fieldType,
        },
        type: attributeType,
        // Planned values (not associated with wells) aren't used in PlateRecords
        plannedValues: [],
      },
      displayName: displayNameOverride ?? fieldName,
      icon: getIconFromFieldType(fieldType as SupportedFieldType),
    };
  });
  return Object.values(wellAnnotationMap);
}

/**
 * Build a list of display layer dropdown options for the given list of data parameter values.
 */
export function getDisplayLayerDropdownOptionsForDataParameterValues(
  dataParameterValues: Array<SupportedFrozenDataParameterValue>,
  attributeType: SchematizedAttributeType,
  supportedTypes: Set<FieldType>
): Array<SchematizedDisplayLayerMetadata> {
  return getDisplayLayerDropdownOptionsForValues(
    dataParameterValues.map((dataParameterValue) => ({
      key: dataParameterValue.dataParameterVersionId,
      fieldName: dataParameterValue.frozenSchemaFieldValue.frozenMetadata.fieldName,
      fieldType: dataParameterValue.frozenSchemaFieldValue.frozenMetadata.fieldType,
      displayNameOverride: dataParameterValue.dataParameterName,
    })),
    attributeType,
    supportedTypes
  );
}

/**
 * Build a list of display layer dropdown options for the given list of frozen schematized values.
 */
export function getDisplayLayerDropdownOptionsForFrozenValues(
  frozenValues: Array<SupportedFrozenSchemaFieldValue>,
  attributeType: SchematizedAttributeType,
  supportedTypes: Set<FieldType>
): Array<SchematizedDisplayLayerMetadata> {
  return getDisplayLayerDropdownOptionsForValues(
    frozenValues.map((frozenValue) => ({
      key: frozenValue.schemaFieldValue.fieldDefinitionId,
      fieldName: frozenValue.frozenMetadata.fieldName,
      fieldType: frozenValue.frozenMetadata.fieldType,
    })),
    attributeType,
    supportedTypes
  );
}

/**
 * Build a list of display layer dropdown options for the given list of live schematized values.
 */
export function getDisplayLayerDropdownOptionsForLiveValues(
  values: Array<{key: string; schemaTag: MinimalSchemaTag; displayNameOverride?: string | null}>,
  attributeType: SchematizedAttributeType,
  supportedTypes: Set<FieldType>
): Array<SchematizedDisplayLayerMetadata> {
  return getDisplayLayerDropdownOptionsForValues(
    values.map((value) => ({
      key: value.key,
      fieldName: value.schemaTag.tagSchemaField.name,
      fieldType: value.schemaTag.tagSchemaField.fieldType,
      displayNameOverride: value.displayNameOverride,
    })),
    attributeType,
    supportedTypes
  );
}

function getFrozenEntityChipDataHelper(
  apiId: string,
  frozenIconId: BenchlingIconName | undefined,
  frozenName: string | null | undefined,
  liveData: {[id: string]: {name: string; iconId?: BenchlingIconName}}
): FrozenEntityChipData {
  return {
    name: frozenName ?? liveData[apiId]?.name ?? `Inaccessible ${typeToName(APIUtil.apiIdToType(apiId))}`,
    iconId: frozenIconId ?? liveData[apiId]?.iconId ?? apiIdToIconName(apiId),
    isInaccessible: liveData[apiId] == null,
  };
}

/**
 * Make a map from (bioentity, container) API ID to frozen data about that entity for all entities represented
 * in the given transfer records.
 */
export function getFrozenEntityChipData<TMeasurement extends PlateRecordMeasurement | PlateMapMeasurement>(
  transferRecords: Array<ContainerTransferRecord<TMeasurement>>,
  entityChipData: {[id: string]: {name: string; iconId?: BenchlingIconName}}
): {
  [id: string]: FrozenEntityChipData;
} {
  return transferRecords.reduce<{
    [id: string]: FrozenEntityChipData;
  }>((acc, transferRecord) => {
    for (const source of transferRecord.sources) {
      if (isContainerSource(source)) {
        acc[source.containerApiId] ??= getFrozenEntityChipDataHelper(
          source.containerApiId,
          source.frozenSchemaMetadata?.iconId,
          source.containerName,
          entityChipData
        );
        for (const content of source.contents) {
          acc[content.content.entityApiId] ??= getFrozenEntityChipDataHelper(
            content.content.entityApiId,
            content.content.frozenSchemaMetadata?.iconId,
            content.content.entityName,
            entityChipData
          );
        }
      } else {
        acc[source.entityApiId] ??= getFrozenEntityChipDataHelper(
          source.entityApiId,
          source.frozenSchemaMetadata?.iconId,
          source.entityName,
          entityChipData
        );
      }
    }
    for (const content of transferRecord.contents) {
      acc[content.content.entityApiId] ??= getFrozenEntityChipDataHelper(
        content.content.entityApiId,
        content.content.frozenSchemaMetadata?.iconId,
        content.content.entityName,
        entityChipData
      );
    }
    return acc;
  }, {});
}

/**
 * Convert a plate record or plate design into a read-only plate map.
 *
 * By sharing an implementation for the two use cases, we reduce the
 * likelihood of divergent behavior between the two related features as they
 * are developed. This would get simpler if we are able to embrace composition,
 * i.e. both share the PlateMap type.
 */
function convertToPlateMap<
  TCoordinatesKey extends string,
  TTransferKey extends string,
  TMeasurement extends PlateRecordMeasurement | PlateMapMeasurement,
>({
  positions,
  coordinatesKey,
  transferKey,
  plateApiId,
  schemaIdsByReagentId,
  unitsById,
  testOrderInfoById,
  measurementLayers,
}: {
  positions: Array<BaseReadonlyPlateWellTransfer<TCoordinatesKey, TTransferKey, TMeasurement>>;
  coordinatesKey: TCoordinatesKey;
  transferKey: TTransferKey;
  plateApiId: string | null;
  schemaIdsByReagentId: {[id: string]: string | undefined};
  unitsById: UnitsById;
  testOrderInfoById?: TestOrderInfoById;
  measurementLayers?: Array<MeasurementLayer> | null;
}): PlateMap {
  const wellRecordsByPosition = groupByUniqueKey(
    positions,
    (position) => `${position[coordinatesKey].row},${position[coordinatesKey].column}}`
  );
  const dataParameterNamesByVersionId: {[schemaFieldId: string]: string} = {};
  const wells: Array<PlateMapWell> = [];
  const plateSize: PlateMapSize = {
    numRows: Math.max(...positions.map((position) => position[coordinatesKey].row)) + 1,
    numColumns: Math.max(...positions.map((position) => position[coordinatesKey].column)) + 1,
  };

  for (const {row, column} of wellPositionIterator(plateSize)) {
    const wellRecord = wellRecordsByPosition[`${row},${column}}`];
    if (wellRecord == null) {
      wells.push({
        position: {row, column},
        role: null,
        sources: [],
        contents: [],
        schemaTags: {},
        dataParameterTags: {},
        quantity: getDefaultMeasurement(),
        finalQuantity: getDefaultMeasurementWithErrors(),
        concentrationLayers: {},
      });
    } else {
      Object.assign(
        dataParameterNamesByVersionId,
        objectFromEntries(
          wellRecord.dataParameterValues
            ?.filter((param) => param.dataParameterName != null)
            .map((param) => [param.dataParameterVersionId, param.dataParameterName!]) ?? []
        )
      );
      wells.push({
        position: {row, column},
        role: wellRecord.role,
        sources: wellRecord[transferKey].sources.map((source) =>
          getSourceFromTransferRecordSource(source, unitsById, schemaIdsByReagentId)
        ),
        contents: wellRecord[transferKey].contents.map((containerContent) => ({
          type: ReagentType.WELL_CONTENT,
          id: containerContent.content.entityApiId,
          groupId:
            containerContent.content.frozenSchemaMetadata?.schemaApiId ??
            schemaIdsByReagentId[containerContent.content.entityApiId] ??
            INACCESSIBLE_GROUP_ID_SENTINEL,
          initialConcentration: hydrateTransferRecordMeasurement(
            containerContent.content.concentration,
            unitsById
          ),
          finalConcentration: hydrateTransferRecordMeasurementWithErrors(
            containerContent.finalConcentration,
            unitsById
          ),
          materialInput: {
            materialInputId: containerContent.materialInputParameterVersionId ?? NO_MATERIAL_INPUT_SENTINEL,
            error: null,
          },
          testOrders: containerContent.testOrders?.map(({testOrderId}) => ({id: testOrderId})),
        })),
        schemaTags: getSchemaTags(wellRecord.schemaFieldValues),
        dataParameterTags: getDataParameterTags(wellRecord.dataParameterValues ?? []),
        quantity: getDefaultMeasurement(),
        finalQuantity: hydrateTransferRecordMeasurementWithErrors(
          wellRecord[transferKey].finalQuantity,
          unitsById
        ),
        concentrationLayers: Object.fromEntries(
          (wellRecord.measurementLayerValues ?? []).map((measurementLayerValue) => [
            measurementLayerValue.layerId,
            hydrateTransferRecordMeasurement(measurementLayerValue.value, unitsById),
          ])
        ),
      });
    }
  }
  return {
    id: plateApiId,
    size: plateSize,
    wells,
    dataParameterNamesByVersionId,
    testOrderDilutionInfoById: testOrderInfoById,
    concentrationLayerInfoById: Object.fromEntries(
      (measurementLayers ?? []).map((measurementLayer) => [measurementLayer.layerId, measurementLayer])
    ),
  };
}

/**
 * Transform the given plate record to a plate map for display.
 */
export function getPlateRecordAsPlateMap(
  plateRecord: PlateRecord,
  schemaIdsByReagentId: {[id: string]: string | undefined},
  unitsById: UnitsById,
  testOrderInfoById: TestOrderInfoById
): PlateMap {
  return convertToPlateMap({
    positions: plateRecord.wellTransferRecords,
    coordinatesKey: 'position',
    transferKey: 'transferRecord',
    plateApiId: plateRecord.plateApiId,
    schemaIdsByReagentId,
    unitsById,
    testOrderInfoById,
  });
}

/**
 * Transform the given plate design to a plate map for display.
 */
export function getPersistedPlateMapAsPlateMap(
  plateMap: PersistedPlateMap,
  schemaIdsByReagentId: {[id: string]: string | undefined},
  unitsById: UnitsById
): PlateMap {
  return convertToPlateMap({
    positions: plateMap.positions,
    coordinatesKey: 'coordinates',
    transferKey: 'transfer',
    plateApiId: null,
    schemaIdsByReagentId,
    unitsById,
    // Test orders not currently supported for plate designs
    testOrderInfoById: undefined,
    measurementLayers: plateMap.measurementLayers,
  });
}

/**
 * For the given plate record / design, get all the reagent assignment groups needed for the plate map based
 * on schema.
 *
 * If any reagent has no frozen schema data associated, this function will return null. If this occurs, the
 * caller should live-fetch all data (e.g. via createAssignmentGroupsForAttribute).
 */
export function getReagentAssignmentGroupsFromWells<
  TTransferKey extends string,
  TMeasurement extends PlateRecordMeasurement | PlateMapMeasurement,
>(
  positions: Array<BaseReadonlyPlateWellTransfer<never, TTransferKey, TMeasurement>>,
  transferKey: TTransferKey
): {
  [id: string]: ReagentAssignmentGroup;
} | null {
  const groupsById: {[id: string]: ReagentAssignmentGroup} = {};
  const colorGenerator = getColor({});

  function addGroup(frozenSchema: FrozenSchemaMetadata): void {
    if (groupsById[frozenSchema.schemaApiId] == null) {
      groupsById[frozenSchema.schemaApiId] = {
        id: frozenSchema.schemaApiId,
        color: colorGenerator.next().value,
        name: frozenSchema.name,
        value: {id: frozenSchema.schemaApiId, iconId: frozenSchema.iconId},
      };
    }
  }

  for (const position of positions) {
    const transferRecord = position[transferKey];
    for (const {content} of transferRecord.contents) {
      if (content.frozenSchemaMetadata == null) {
        return null;
      }
      addGroup(content.frozenSchemaMetadata);
    }
    for (const source of transferRecord.sources) {
      if (isContainerSource(source)) {
        if (source.contents.length === 0) {
          if (source.frozenSchemaMetadata == null) {
            return null;
          }
          addGroup(source.frozenSchemaMetadata);
        } else {
          for (const {content} of source.contents) {
            if (content.frozenSchemaMetadata == null) {
              return null;
            }
            addGroup(content.frozenSchemaMetadata);
          }
        }
      } else {
        if (source.frozenSchemaMetadata == null) {
          return null;
        }
        addGroup(source.frozenSchemaMetadata);
      }
    }
  }
  return groupsById;
}
